import * as React from 'react';
import { useEffect, useState } from 'react';
import moment from 'moment';
import PhoneOutlinedIcon from '@material-ui/icons/PhoneOutlined';
import LabelOutlinedIcon from '@material-ui/icons/LabelOutlined';
import {
    retrieveTasksSummaryData,
    TaskSummary,
    TaskStatusEnum,
    TasksSummaryQueryParams,
} from '../../../tasksSummary/TasksSummaryRepository';
import { browserHistory } from '../../../components/AppContent';
import { linkToPage, showNotification } from '../../../App';
import { DueListRow, DueListWidget } from './DueListWidget';
import { formatDueLabel } from './formatDueLabel';
import EventSystem from '../../../EventSystem';
import { QUICK_VIEW_REFRESH_EVENT } from '../QuickViewDrawer';
import { runWithMinimumVisibleRefresh } from './RefreshingIndicator';

const DUE_LIST_ROW_LIMIT = 5;

// Contact follow-ups due today or earlier - same task.summaryList field TasksDueWidget.tsx
// uses, told apart only by isFromDataSource/isIncludeFollowUpTasksOnly, matching the mobile
// app's own ContactFollowUpsDueCardStack.tsx params exactly (isFromDataSource: false,
// isIncludeFollowUpTasksOnly: true - vs Tasks Due's isFromDataSource: true,
// isIncludeFollowUpTasksOnly: null).
export interface ContactFollowUpsDueWidgetProps {
    /** Opens the task inline (TasksSummaryFormDialog/PhoneFormDialog/EnquiryFormDrawer, routed and mounted once in QuickViewDrawer.tsx) rather than navigating away - explicit request. */
    onOpenTask: (task: TaskSummary) => void;
}

// Static half of the query params, factored out of the effect below so the infinite-scroll
// load-more call can build the identical filter with just a different offset/first, without
// repeating this whole object a second time.
function buildParams(offset: number, first: number): TasksSummaryQueryParams {
    return {
        offset,
        first,
        filter: null,
        sortColumn: 'DueDate',
        sortDirection: 'ASC',
        taskStatus: TaskStatusEnum.Open,
        isIncludeCrmTasks: false,
        isMyTasksOnly: false,
        isPersonalTask: true,
        isFromDataSource: false,
        groupBy: null,
        groupByValue: null,
        completedFromDate: null,
        completedToDate: null,
        updatedFromDate: null,
        updatedToDate: null,
        priorityList: null,
        statusList: null,
        allocatedToList: null,
        allocatedByList: null,
        created: null,
        due: 'todayAndEarlier',
        teamList: null,
        taskTypes: null,
        clientList: null,
        matterList: null,
        isIncludeFollowUpTasksOnly: true,
        isIncludeHasTasks: null,
    };
}

export const ContactFollowUpsDueWidget: React.FC<ContactFollowUpsDueWidgetProps> = (props) => {
    const [tasks, setTasks] = useState<TaskSummary[] | null>(null);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    // Drives DueListWidget's own RefreshingIndicator - see YourEnquiriesWidget.tsx's identical
    // comment for why this is a separate flag from isLoading (which only the initial mount
    // touches).
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Factored out of the mount effect below so Quick View's own header Refresh button
    // (QuickViewDrawer.tsx) can call the exact same first-page fetch again, resetting the
    // list back to page 1 rather than re-requesting however many rows infinite-scroll had
    // loaded (onLoadMore's own accumulated offset is separate from this).
    const fetchFirstPage = (onSuccess: (tasks: TaskSummary[], totalCount: number) => void, onSettled?: () => void) => {
        retrieveTasksSummaryData(
            buildParams(0, DUE_LIST_ROW_LIMIT),
            true,
            (data) => {
                onSuccess(data.task.summaryList.tasks, data.task.summaryList.actualRowCountForSearch);
                onSettled?.();
            },
            // tslint:disable-next-line: no-any
            (reason: any) => {
                showNotification('Failed to Fetch Contact Follow-ups Due', reason, 'error');
                onSettled?.();
            },
            // batchGroup opts this widget's query out of the default shared batch bucket -
            // see YourEnquiriesWidget.tsx's identical comment.
            { batchGroup: 'quickView-contactFollowUpsDue' },
        );
    };

    useEffect(() => {
        fetchFirstPage((tasksData, totalCountData) => {
            setTasks(tasksData);
            setTotalCount(totalCountData);
            setIsLoading(false);
        });
    // tslint:disable-next-line: align
    }, []);

    // See YourEnquiriesWidget.tsx's own identical comment for why fetchFirstPageRef/the
    // empty-deps effect/the explicit cleanup are each needed. Deliberately doesn't touch
    // isLoading (only the initial mount does), so a manual refresh swaps the rows in place
    // instead of flashing the whole card back to its skeleton.
    const fetchFirstPageRef = React.useRef(fetchFirstPage);
    fetchFirstPageRef.current = fetchFirstPage;
    useEffect(() => {
        const onRefresh = () => {
            runWithMinimumVisibleRefresh(setIsRefreshing, () => new Promise<void>((resolve) => {
                fetchFirstPageRef.current(
                    (tasksData, totalCountData) => {
                        setTasks(tasksData);
                        setTotalCount(totalCountData);
                    },
                    () => resolve()
                );
            }));
        };
        EventSystem.on(QUICK_VIEW_REFRESH_EVENT, onRefresh);
        return () => { EventSystem.event.removeListener(QUICK_VIEW_REFRESH_EVENT, onRefresh); };
    // tslint:disable-next-line: align
    }, []);

    const onOpenTasksSummary = () => {
        browserHistory.push(linkToPage('tasksSummary'));
    };

    const hasMore = (tasks?.length ?? 0) < totalCount;

    // Infinite-scroll load-more (explicit request) - fetches the next page at the current
    // row count's offset and appends it onto what's already loaded.
    const onLoadMore = () => {
        if (isLoadingMore || !hasMore || !tasks) {
            return;
        }
        setIsLoadingMore(true);
        retrieveTasksSummaryData(
            buildParams(tasks.length, DUE_LIST_ROW_LIMIT),
            true,
            (data) => {
                setTasks((previousTasks) => [...(previousTasks ?? []), ...data.task.summaryList.tasks]);
                setIsLoadingMore(false);
            },
            // tslint:disable-next-line: no-any
            (reason: any) => {
                setIsLoadingMore(false);
                showNotification('Failed to Fetch Contact Follow-ups Due', reason, 'error');
            },
            { batchGroup: 'quickView-contactFollowUpsDue' },
        );
    };

    const rows: DueListRow[] | null = tasks?.map((task) => {
        const due = formatDueLabel(task.dueDate);
        const primary = task.client ?? task.taskDescription ?? 'Contact';
        // the contact's name is the bold text, so the avatar carries its first letters (e.g. "Jane Rutter" -> "JR")
        const initials = primary
            .trim()
            .split(/\s+/)
            .map(word => word[0])
            .filter(Boolean)
            .slice(0, 2)
            .join('')
            .toUpperCase() || '?';
        return {
            key: task.taskGuidID,
            primary,
            secondary: task.taskDescription ?? undefined,
            dueLabel: due.label,
            isOverdue: due.isOverdue,
            onClick: () => props.onOpenTask(task),
            initials,
            statusLabel: task.taskStatusDescription,
            categoryLabel: task.taskTypeDescription,
            updatedLabel: task.updatedDate ? moment(task.updatedDate).format('D MMM YYYY') : null,
        };
    }) ?? null;

    return (
        <DueListWidget
            icon={<PhoneOutlinedIcon fontSize="small" />}
            iconBg="#fbeaf0"
            iconColor="#993556"
            title="Contact Follow-ups Due"
            isLoading={isLoading}
            isRefreshing={isRefreshing}
            rows={rows}
            totalCount={totalCount}
            emptyMessage="Nothing due on your contact follow-ups right now."
            onViewAll={onOpenTasksSummary}
            onLoadMore={onLoadMore}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            variant="card"
            categoryIcon={<LabelOutlinedIcon />}
        />
    );
};
